import React, { useState } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons/faTriangleExclamation';
import clsx from 'clsx';
import type { MailboxProviderId } from 'fym-shared/src/music24/outreach/mailboxProviders';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { useMyTeamRole } from '../../../team/useMyTeamRole';
import { SMLUpgradeFeature } from '../../config/music24PlanCapabilities';
import { useUpgradePrompt } from '../../hooks/useUpgradePrompt';
import { MAILBOX_GUIDE_COPY } from './mailboxGuideCopy';
import { GuideError, GuideErrorReason, providerOf } from './mailboxGuideState';
import { mailboxGuideMessages as messages } from './mailboxGuideMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the guide; each part uses its own slice of it */
import styles from './MailboxGuide.module.scss';
/* eslint-enable css-modules/no-unused-class */

const COPY: Record<
  Exclude<GuideErrorReason, 'authFailed' | 'mailboxLimit'>,
  { title: MessageDescriptor; body: MessageDescriptor }
> = {
  normalPasswordUsed: { title: messages.errNormalTitle, body: messages.errNormalBody },
  hostUnreachable: { title: messages.errHostTitle, body: messages.errHostBody },
  tlsFailed: { title: messages.errTlsTitle, body: messages.errTlsBody },
  smtpAuthDisabled: { title: messages.errSmtpTitle, body: messages.errSmtpBody },
  imapDisabled: { title: messages.errImapTitle, body: messages.errImapBody },
  providerUnsupported: { title: messages.errUnsupportedTitle, body: messages.errUnsupportedBody },
  taken: { title: messages.errTakenTitle, body: messages.errTakenBody },
  supportPaused: { title: messages.errSupportPausedTitle, body: messages.errSupportPausedBody },
  demoReadOnly: { title: messages.errDemoTitle, body: messages.errDemoBody },
  unknown: { title: messages.errUnknownTitle, body: messages.errUnknownBody },
};

interface MailboxErrorBoxProps {
  error: GuideError;
  providerId: MailboxProviderId;
  /** The IMAP host of the check (the "We could not reach" line). */
  imapHost: string;
  onShowHow: () => void;
  onEditServers: () => void;
  onAdmin: () => void;
  onUseAnother: () => void;
}

/**
 * Why the check or the connect failed, in plain words (design outreach-v2 o6): one copy for each
 * reason, never an SMTP code. "Show what the server answered" opens the hidden server text.
 */
const MailboxErrorBoxComponent: React.FC<MailboxErrorBoxProps> = ({
  error,
  providerId,
  imapHost,
  onShowHow,
  onEditServers,
  onAdmin,
  onUseAnother,
}) => {
  const intl = useIntl();
  const isOwner = useMyTeamRole() === 'OWNER';
  const openUpgradePrompt = useUpgradePrompt();
  const [showAnswer, setShowAnswer] = useState(false);
  const provider = providerOf(providerId);
  const name = intl.formatMessage(MAILBOX_GUIDE_COPY[providerId].label);
  const createUrl = provider.steps.find((step) => step.key === 'create')?.linkUrl;

  const text = () => {
    if (error.reason === 'authFailed') {
      return {
        title: intl.formatMessage(messages.errAuthTitle, { provider: name }),
        body: intl.formatMessage(
          provider.passwordKind === 'appPassword' ? messages.errAuthBodyApp : messages.errAuthBodyEmail,
          { provider: name }
        ),
      };
    }
    if (error.reason === 'mailboxLimit') {
      // The owner picks the plan; a member asks the owner (the plan prompt is the owner's).
      return {
        title: intl.formatMessage(messages.errLimitTitle),
        body: intl.formatMessage(isOwner ? messages.errLimitBodyOwner : messages.errLimitBodyMember),
      };
    }
    const copy = COPY[error.reason];
    return {
      title: intl.formatMessage(copy.title, { provider: name, host: imapHost }),
      body: intl.formatMessage(copy.body, { provider: name }),
    };
  };

  const action = () => {
    switch (error.reason) {
      case 'authFailed':
        return createUrl === undefined ? null : (
          <a className="btn btn-sm btn-primary" href={createUrl} target="_blank" rel="noopener noreferrer">
            {intl.formatMessage(MAILBOX_GUIDE_COPY[providerId].steps.create?.link ?? messages.showMeHow)}
          </a>
        );
      case 'normalPasswordUsed':
        return (
          <SMLButton size="sm" color="primary" omitCap onClick={onShowHow}>
            {intl.formatMessage(messages.showMeHow)}
          </SMLButton>
        );
      case 'hostUnreachable':
      case 'tlsFailed':
        return (
          <SMLButton size="sm" outline omitCap onClick={onEditServers}>
            {intl.formatMessage(messages.editServers)}
          </SMLButton>
        );
      case 'smtpAuthDisabled':
        return (
          <SMLButton size="sm" color="primary" omitCap onClick={onAdmin}>
            {intl.formatMessage(messages.adminMessageButton)}
          </SMLButton>
        );
      case 'providerUnsupported':
        return (
          <SMLButton size="sm" color="primary" omitCap onClick={onUseAnother}>
            {intl.formatMessage(messages.useAnother)}
          </SMLButton>
        );
      case 'mailboxLimit':
        return isOwner ? (
          <SMLButton
            size="sm"
            color="primary"
            omitCap
            onClick={() => openUpgradePrompt(SMLUpgradeFeature.outreachMailboxes)}
          >
            {intl.formatMessage(messages.seePlans)}
          </SMLButton>
        ) : null;
      case 'imapDisabled':
      case 'supportPaused':
      case 'demoReadOnly':
      case 'taken':
      case 'unknown':
      default:
        return null;
    }
  };

  const { title, body } = text();
  const isWarn = error.reason === 'smtpAuthDisabled' || error.reason === 'imapDisabled';
  return (
    <div
      className={clsx(styles.box, isWarn ? styles.boxWarn : styles.boxNeg)}
      role="alert"
      data-testid={`mailbox-error-${error.reason}`}
    >
      <FontAwesomeIcon icon={faTriangleExclamation} className={styles.boxIcon} />
      <div className="flex-grow-1">
        <b>{title}</b>
        {body}
        <div className={styles.actions}>
          {action()}
          {error.detail !== '' && (
            <SMLButton size="sm" color="link" omitCap onClick={() => setShowAnswer(!showAnswer)}>
              {intl.formatMessage(messages.showAnswer)}
            </SMLButton>
          )}
        </div>
        {showAnswer && <p className={styles.answer}>{error.detail}</p>}
      </div>
    </div>
  );
};

export const MailboxErrorBox = TypedMemo(MailboxErrorBoxComponent);
